import * as React from 'react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ALBUMS } from '@disco/constants/FeatureTypes'
import { CUSTOM_CHANNEL, MAX_NESTING } from '@disco/constants/GroupTypes'
import type { ViewType } from '@disco/constants/ViewTypes'
import { ALBUM_VIEW, DEFAULT_VIEW } from '@disco/constants/ViewTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  deleteGroup,
  updateGroup,
} from '@disco/state-management/actions/GroupActions'
import { makeIsFeatureEnabled } from '@disco/state-management/selectors/business'
import { getIsSubfolder } from '@disco/state-management/selectors/groups'
import { getUser } from '@disco/state-management/selectors/user'

import type { Group } from 'types/group'
import type { PopoverItemType } from 'types/ui'

import EditCustomLibraryForm from 'components/common/custom-libraries/edit-form'
import RenameDialog from 'components/common/rename-dialog'
import { channelViewOptions } from 'components/explorer/pane/search-header/select-options'

import { useDispatch, useSelector } from 'hooks/use-redux'

import DeleteGroupDialog from '../../modals/dialogs/delete-group-dialog'

type UseGroupMenuOptions = {
  group: Group | null | undefined
  // Top-level channels don't pass a depth; treat them as depth 1, same as a
  // top-level folder.
  groupDepth?: number
  pane?: { view: ViewType | null | undefined }
  sortingOptions?: PopoverItemType[]
}

/**
 * Folder/channel menu state and handlers, shared by the three-dot menu
 * (GroupItemActions) and the row's right-click menu (GroupContextMenu) so
 * the two can't drift apart.
 */
export const useGroupMenu = ({
  group,
  groupDepth = 1,
  pane,
  sortingOptions,
}: UseGroupMenuOptions) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = useSelector(getUser)

  const isFeatureEnabled = useMemo(() => makeIsFeatureEnabled(), [])
  const albumsEnabled = useSelector(state =>
    isFeatureEnabled(state, { featureName: ALBUMS }),
  )
  const isSubfolder = useSelector(state =>
    group ? getIsSubfolder(state, { groupId: group.id }) : false,
  )

  const [showNewGroupModal, setShowNewGroupModal] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showEditGroupModal, setShowEditGroupModal] = useState(false)
  const [showRenameDialog, setShowRenameDialog] = useState(false)

  const handleRename = (name: string) => {
    if (!group) return
    dispatch(updateGroup({ id: group.id, name }))
    setShowRenameDialog(false)
  }

  const handleRemoveClick = () => {
    if (group) dispatch(deleteGroup(group))
    setShowDeleteDialog(false)
  }

  const handleShareClick = () => {
    if (!group) return
    navigate(`/explorer/channels/${group.id}/share`)
  }

  const handleMenuOpen = () => {
    dispatch(
      sendMixpanelEvent('Open Menu', {
        Context: 'Channel',
        Menu: 'Channel',
        Location: pane ? 'Explorer' : 'Sidebar',
      }),
    )
  }

  const handleMenuAction = (value: PopoverItemType['value']) => {
    switch (value) {
      case 'share':
        handleShareClick()
        break
      case 'add_folder':
        setShowNewGroupModal(true)
        break
      case 'edit_channel':
        setShowEditGroupModal(true)
        break
      case 'edit_folder':
        setShowRenameDialog(true)
        break
      case 'delete':
        setShowDeleteDialog(true)
        break
    }
  }

  if (!group) {
    return {
      options: [] as PopoverItemType[],
      handleMenuAction,
      handleMenuOpen,
      dialogs: null as React.ReactNode,
    }
  }

  const isChannel = !group.parent
  const { permissions, business_role } = group
  const viewOptions = albumsEnabled
    ? channelViewOptions
    : channelViewOptions.filter(option => option.value !== ALBUM_VIEW)
  const isFolder = !isChannel && !isSubfolder
  const folderType = isChannel ? 'Add folder' : isFolder && 'Add subfolder'
  const isRenameVisible = business_role === 'owner'
  const isRenameDisabled = !permissions.rename
  const isCreateVisible = business_role === 'owner'
  const isCreateFolderDisabled =
    !permissions.structure || groupDepth >= MAX_NESTING
  const isDeleteVisible = business_role === 'owner'
  const isDeleteDisabled = !permissions.delete
  const isLeaveChannelVisible = isChannel && business_role === 'partner'
  const isLeaveChannelDisabled = !permissions.manage
  const isShareVisible = isChannel || (!isChannel && permissions.share_link)
  const isShareDisabled = !permissions.share_link && !permissions.manage

  const options: PopoverItemType[] = []

  if (isShareVisible) {
    options.push({
      value: 'share',
      label: 'Share',
      disabled: isShareDisabled,
      tooltip: isShareDisabled
        ? 'Only channel managers can do this'
        : undefined,
    })
  }

  if (pane) {
    options.push({
      value: 'show',
      label: 'Show',
      nested: viewOptions.map(option => {
        if ((pane.view || DEFAULT_VIEW) === option.value)
          return {
            selected: true,
            ...option,
            icon: undefined,
          }
        return { ...option, icon: undefined }
      }),
    })
    options.push({
      value: 'sort',
      label: 'Sorting',
      nested: sortingOptions,
    })
  }

  if (folderType && isCreateVisible) {
    options.push({
      value: 'add_folder',
      disabled: isCreateFolderDisabled,
      label: folderType,
    })
  }

  if (isRenameVisible) {
    options.push({
      value: group.parent ? 'edit_folder' : 'edit_channel',
      label: 'Rename',
      disabled: isRenameDisabled,
      tooltip: isRenameDisabled
        ? 'Only channel managers can do this'
        : undefined,
    })
  }

  if (isDeleteVisible) {
    options.push({
      value: 'delete',
      label: 'Delete',
      destructive: true,
      disabled: isDeleteDisabled,
      tooltip: isDeleteDisabled
        ? 'Only channel managers can do this'
        : undefined,
    })
  }

  if (isLeaveChannelVisible) {
    options.push({
      value: 'delete',
      label: 'Leave channel',
      destructive: true,
      disabled: isLeaveChannelDisabled,
      tooltip: isLeaveChannelDisabled
        ? 'Only channel managers can do this'
        : undefined,
    })
  }

  const dialogs = (
    <>
      {showNewGroupModal && (
        <EditCustomLibraryForm
          user={user}
          groupType={CUSTOM_CHANNEL}
          parentGroup={group}
          onSubmit={() => setShowNewGroupModal(false)}
          onClose={() => setShowNewGroupModal(false)}
        />
      )}
      {showDeleteDialog && (
        <DeleteGroupDialog
          group={group}
          onConfirm={handleRemoveClick}
          onCancel={() => setShowDeleteDialog(false)}
        />
      )}
      {showEditGroupModal && (
        <EditCustomLibraryForm
          user={user}
          {...group}
          groupType={CUSTOM_CHANNEL}
          onSubmit={() => setShowEditGroupModal(false)}
          onClose={() => setShowEditGroupModal(false)}
        />
      )}
      {showRenameDialog && (
        <RenameDialog
          typeName={isSubfolder ? 'subfolder' : 'folder'}
          initialValue={group.name}
          onSubmit={handleRename}
          onCancel={() => setShowRenameDialog(false)}
        />
      )}
    </>
  )

  return { options, handleMenuAction, handleMenuOpen, dialogs }
}
